/**
 * Personal-event color palette. Tailwind can't build class names from runtime
 * values, so the full class strings are enumerated here and selected by key.
 * Callbacks/tasks have fixed colors (cyan / amber) set in the calendar page;
 * these are only for user-owned events.
 */
export interface EventColor {
  key: string;
  label: string;
  /** Chip background + text. */
  chip: string;
  /** Solid dot / accent. */
  dot: string;
  /** Left border accent for agenda rows. */
  border: string;
}

export const EVENT_COLORS: EventColor[] = [
  { key: 'slate',   label: 'Slate',   chip: 'bg-slate-100 text-slate-700 dark:bg-slate-700/50 dark:text-slate-200',     dot: 'bg-slate-500',   border: 'border-slate-400' },
  { key: 'indigo',  label: 'Indigo',  chip: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300', dot: 'bg-indigo-500',  border: 'border-indigo-400' },
  { key: 'violet',  label: 'Violet',  chip: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300', dot: 'bg-violet-500',  border: 'border-violet-400' },
  { key: 'emerald', label: 'Emerald', chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', dot: 'bg-emerald-500', border: 'border-emerald-400' },
  { key: 'rose',    label: 'Rose',    chip: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',         dot: 'bg-rose-500',    border: 'border-rose-400' },
  { key: 'amber',   label: 'Amber',   chip: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',     dot: 'bg-amber-500',   border: 'border-amber-400' },
  { key: 'cyan',    label: 'Cyan',    chip: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',         dot: 'bg-cyan-500',    border: 'border-cyan-400' },
];

export const DEFAULT_EVENT_COLOR = 'slate';

export function eventColor(key: string | null | undefined): EventColor {
  return EVENT_COLORS.find((c) => c.key === key) || EVENT_COLORS[0];
}

/**
 * Per-recruiter colors, used only when overlaying several people's calendars
 * (chip fill still encodes the source TYPE — cyan callbacks / amber tasks /
 * slate events — so these are a separate dimension and deliberately avoid the
 * source-type hues cyan/amber/slate to stay distinguishable).
 */
export interface PersonColor {
  /** Solid dot / avatar background. */
  dot: string;
  /** Avatar text on the dot. */
  avatarBg: string;
  /** Left border accent for agenda rows. */
  border: string;
}

const PERSON_COLORS: PersonColor[] = [
  { dot: 'bg-rose-500',    avatarBg: 'bg-rose-500',    border: 'border-rose-400' },
  { dot: 'bg-blue-500',    avatarBg: 'bg-blue-500',    border: 'border-blue-400' },
  { dot: 'bg-emerald-500', avatarBg: 'bg-emerald-500', border: 'border-emerald-400' },
  { dot: 'bg-violet-500',  avatarBg: 'bg-violet-500',  border: 'border-violet-400' },
  { dot: 'bg-fuchsia-500', avatarBg: 'bg-fuchsia-500', border: 'border-fuchsia-400' },
  { dot: 'bg-teal-500',    avatarBg: 'bg-teal-500',    border: 'border-teal-400' },
  { dot: 'bg-indigo-500',  avatarBg: 'bg-indigo-500',  border: 'border-indigo-400' },
  { dot: 'bg-lime-600',    avatarBg: 'bg-lime-600',    border: 'border-lime-500' },
];

const UNASSIGNED_COLOR: PersonColor = { dot: 'bg-gray-400', avatarBg: 'bg-gray-400', border: 'border-gray-300' };

/** Stable color for a recruiter id (hash → palette), independent of team order. */
export function personColor(userId: string | null | undefined): PersonColor {
  if (!userId) return UNASSIGNED_COLOR;
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  return PERSON_COLORS[hash % PERSON_COLORS.length];
}
